import { useQueryClient } from "@tanstack/react-query";
import { Archive, Bot, LayoutDashboard, LogOut, Moon, Search, Settings, Sun } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router";
import { api } from "../lib/api";
import { ROLE_LABEL } from "../lib/format";
import { keys, useMe, useOverview } from "../lib/queries";
import type { OverviewProject } from "../lib/types";
import { Logo } from "./Logo";
import styles from "./AppShell.module.css";

/** The sidebar dot says whether the bot is live: draft yellow, paused black, active green, active but out of saldo red. */
function liveness(project: OverviewProject): { color: string; text: string } {
  if (project.archivedAt) return { color: "rgba(255, 255, 255, 0.3)", text: "diarsipkan" };
  if (project.status === "draft") return { color: "var(--warning)", text: "draf" };
  if (project.status === "paused") return { color: "#000", text: "dijeda" };
  return project.level === "empty" ? { color: "var(--critical)", text: "aktif, saldo habis" } : { color: "var(--good)", text: "aktif" };
}

export function AppShell() {
  const me = useMe().data!;
  const client = me.role === "client";
  // A client account sees one project and no portfolio, so it never asks for one.
  const overview = useOverview(30, !client);
  const queryClient = useQueryClient();
  const location = useLocation();
  const [filter, setFilter] = useState("");
  const [theme, setTheme] = useTheme();

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  const [projects, archived] = useMemo(() => {
    const matches = (overview.data?.projects ?? []).filter((project) => `${project.name} ${project.clientName}`.toLowerCase().includes(filter.toLowerCase()));
    return [matches.filter((project) => !project.archivedAt), matches.filter((project) => project.archivedAt)];
  }, [overview.data, filter]);

  const projectLink = (project: OverviewProject) => {
    const live = liveness(project);
    return (
      <li key={project.id}>
        <NavLink to={`/projects/${project.id}`} className={({ isActive }) => `${styles.project} ${isActive ? styles.active : ""}`} title={`${project.name} · ${live.text}`}>
          <span className={`${styles.dot} ${project.status === "paused" && !project.archivedAt ? styles.dotPaused : ""}`} style={{ background: live.color }} aria-hidden />
          <span className={styles.projectName}>{project.name}</span>
          <span className="sr-only">{live.text}</span>
        </NavLink>
      </li>
    );
  };

  return (
    <div className={styles.shell}>
      <aside className={styles.sidebar} data-print-hide>
        <div className={styles.brand}>
          <Logo />
          <div>
            <div className={styles.brandName}>Navibiz Console</div>
            <div className={styles.brandSub}>AI Chatbot · Kenali Kami</div>
          </div>
        </div>

        <nav className={styles.nav} aria-label="Navigasi utama">
          {client && me.projectId ? (
            <NavLink to={`/projects/${me.projectId}`} className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ""}`}>
              <Bot aria-hidden /> Chatbot saya
            </NavLink>
          ) : (
            <NavLink to="/" end className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ""}`}>
              <LayoutDashboard aria-hidden /> Portofolio
            </NavLink>
          )}
          <NavLink to="/settings" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ""}`}>
            <Settings aria-hidden /> {client ? "Akun" : "Pengaturan"}
          </NavLink>
        </nav>

        {!client && (
        <>
        <div className={styles.projectsHead}>
          <span className="kicker">Proyek klien</span>
          <span className={styles.count}>{overview.data ? projects.length : "—"}</span>
        </div>
        <label className={styles.search}>
          <Search aria-hidden />
          <input placeholder="Cari klien…" value={filter} onChange={(event) => setFilter(event.target.value)} aria-label="Cari proyek" />
        </label>
        <ul className={styles.projects}>{projects.map(projectLink)}</ul>
        {archived.length > 0 && (
          <details className={styles.archive}>
            <summary>
              <Archive aria-hidden /> Arsip ({archived.length})
            </summary>
            <ul className={styles.projects}>{archived.map(projectLink)}</ul>
          </details>
        )}
        </>
        )}

        <div className={styles.user}>
          <div className={styles.avatar} aria-hidden>
            {me.name
              .split(" ")
              .map((part) => part[0])
              .slice(0, 2)
              .join("")}
          </div>
          <div className={styles.userText}>
            <div className={styles.userName}>{me.name}</div>
            <div className={styles.userRole}>{ROLE_LABEL[me.role]}</div>
          </div>
          <button type="button" className={styles.iconButton} onClick={() => setTheme(theme === "dark" ? "light" : "dark")} aria-label="Ganti tema" title="Ganti tema">
            {theme === "dark" ? <Sun aria-hidden /> : <Moon aria-hidden />}
          </button>
          <button
            type="button"
            className={styles.iconButton}
            aria-label="Keluar"
            title="Keluar"
            onClick={async () => {
              await api.post("/auth/logout");
              queryClient.setQueryData(keys.me, null);
              queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "me" });
            }}
          >
            <LogOut aria-hidden />
          </button>
        </div>
      </aside>

      <main className={styles.main}>
        <Outlet />
      </main>
    </div>
  );
}

/** Theme follows the OS until the user picks one; the choice is remembered per browser. */
function useTheme(): ["light" | "dark", (theme: "light" | "dark") => void] {
  const system = () => (matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
  const [theme, setTheme] = useState<"light" | "dark">(() => {
    try {
      return (localStorage.getItem("console-theme") as "light" | "dark" | null) ?? system();
    } catch {
      return system();
    }
  });
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);
  return [
    theme,
    (next) => {
      setTheme(next);
      try {
        localStorage.setItem("console-theme", next);
      } catch {
        /* storage blocked */
      }
    },
  ];
}
